import React from "react";

/**
 * Khoze — Nelurio's study assistant: a friendly wise old professor.
 * Flat-vector inline SVG (no assets) with blink, hover and speaking animations.
 * Per the user's sketch: big round glasses, full white beard, slim robe.
 */
export function KhozeAvatar({ size = 84, speaking = false, wave = false }: { size?: number; speaking?: boolean; wave?: boolean }) {
  return (
    <span className={`khoze ${speaking ? "is-speaking" : ""} ${wave ? "is-waving" : ""}`} style={{ width: size, height: size }} aria-hidden="true">
      <svg viewBox="0 0 120 120" width="100%" height="100%">
        {/* robe / body */}
        <path d="M36 116 C36 96 44 88 60 88 C76 88 84 96 84 116 Z" fill="#6c4cf6" />
        <path d="M55 88 h10 v10 l-5 5 -5 -5 Z" fill="#efeaff" />
        {/* arms */}
        <path d="M38 96 C30 100 26 106 28 112" stroke="#6c4cf6" strokeWidth="9" strokeLinecap="round" fill="none" />
        <path className="khoze__arm" d="M82 96 C90 100 94 106 92 112" stroke="#6c4cf6" strokeWidth="9" strokeLinecap="round" fill="none" />
        {/* book in hand */}
        <g className="khoze__book">
          <rect x="74" y="104" width="26" height="7" rx="3" fill="#ffd9ae" />
          <rect x="76" y="100" width="10" height="5" rx="2" fill="#8b72ff" />
        </g>
        {/* bald head + side hair */}
        <circle cx="60" cy="46" r="26" fill="#f3c89d" />
        <path d="M34 44 a26 26 0 0 1 12 -20 l3 6 a20 20 0 0 0 -9 15 Z" fill="#dcd6e8" />
        <path d="M86 44 a26 26 0 0 0 -12 -20 l-3 6 a20 20 0 0 1 9 15 Z" fill="#dcd6e8" />
        {/* wise forehead lines */}
        <path d="M50 30 q10 -4 20 0 M52 24 q8 -3 16 0" stroke="#d9a878" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        {/* big round glasses (per sketch) */}
        <circle cx="49" cy="46" r="10.5" fill="rgba(255,255,255,.55)" stroke="#3a2f55" strokeWidth="2.6" />
        <circle cx="71" cy="46" r="10.5" fill="rgba(255,255,255,.55)" stroke="#3a2f55" strokeWidth="2.6" />
        <path d="M59.5 46 h1 M38.5 45 l-6 -2 M81.5 45 l6 -2" stroke="#3a2f55" strokeWidth="2.4" strokeLinecap="round" />
        <g className="khoze__eyes">
          <circle cx="49" cy="46" r="3.6" fill="#2b2340" />
          <circle cx="71" cy="46" r="3.6" fill="#2b2340" />
          <circle cx="50.4" cy="44.6" r="1.2" fill="#fff" />
          <circle cx="72.4" cy="44.6" r="1.2" fill="#fff" />
        </g>
        {/* nose */}
        <path d="M60 48 q-3 6 0 8 q3 -2 0 -8" fill="#e8b183" />
        {/* white beard covering lower face */}
        <path d="M40 54 q-2 18 20 26 q22 -8 20 -26 q-8 8 -20 8 q-12 0 -20 -8Z" fill="#f2f0f7" />
        <path d="M52 60 q8 5 16 0 q-2 10 -8 10 q-6 0 -8 -10Z" fill="#ffffff" />
        {/* mustache */}
        <path d="M50 58 q10 6 20 0 q-4 6 -10 6 q-6 0 -10 -6Z" fill="#e8e5f2" />
        {/* graduation cap */}
        <path d="M32 26 L60 14 L88 26 L60 38 Z" fill="#17152f" />
        <rect x="48" y="30" width="24" height="7" rx="2" fill="#232047" />
        <path d="M88 26 v12" stroke="#f4b942" strokeWidth="2.4" strokeLinecap="round" />
        <circle cx="88" cy="41" r="3" fill="#f4b942" />
      </svg>
    </span>
  );
}

/** Khoze identity header used at the top of the assistant chat. */
export function KhozeHeader({ name, tagline }: { name: string; tagline: string }) {
  return (
    <div className="khoze-header">
      <KhozeAvatar size={56} wave />
      <div>
        <strong>{name} <span className="khoze-header__badge">AI</span></strong>
        <p>{tagline}</p>
      </div>
    </div>
  );
}
